import { useCallback, useEffect, useRef, useState } from "react";
import "./portfolio_premium_loader.css";

const LOADER_CYCLE_MS = 4400;

const LOADER_PHASES = [
  { main: "Analyzing your portfolio...", sub: "Reading your holdings", step: 0, pct: 0 },
  { main: "Checking market data...", sub: "Fetching live prices", step: 1, pct: 32 },
  { main: "Running AI analysis...", sub: "Crunching the numbers", step: 2, pct: 65 },
  { main: "Almost ready...", sub: "Building your plan", step: 3, pct: 90 },
];

const PHASE_AT_MS = [0, 1100, 2300, 3300];

const COMPLETE_PHASE = {
  main: "Analysis complete!",
  sub: "Opening your dashboard…",
  step: 4,
  pct: 100,
};

function line_fill_width(line_index, step) {
  if (step >= 4) return "100%";
  if (step > line_index + 1) return "100%";
  if (step === line_index + 1) return "50%";
  return "0%";
}

function PremiumLoaderIllustration() {
  return (
    <div className="illo">
      <svg width="210" height="210" viewBox="0 0 210 210" fill="none" aria-hidden="true">
        <g className="g-pad" style={{ transform: "rotate(7deg)", transformOrigin: "105px 105px" }}>
          <rect x="124" y="26" width="60" height="88" rx="6" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1.2" />
          <rect x="124" y="22" width="60" height="10" rx="4" fill="#94a3b8" />
          <circle cx="132" cy="27" r="2.4" fill="#fff" />
          <circle cx="141" cy="27" r="2.4" fill="#fff" />
          <circle cx="150" cy="27" r="2.4" fill="#fff" />
          <circle cx="159" cy="27" r="2.4" fill="#fff" />
          <circle cx="168" cy="27" r="2.4" fill="#fff" />
          <circle cx="177" cy="27" r="2.4" fill="#fff" />
          <rect x="132" y="42" width="8" height="8" rx="2.5" fill="none" stroke="#cbd5e1" strokeWidth="1.4" />
          <polyline
            className="ck1"
            points="133,46 136,49 140,43.5"
            fill="none"
            stroke="#22c55e"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="144" y="44" width="30" height="4.5" rx="2.2" fill="#e2e8f0" className="ls1" />
          <rect x="132" y="56" width="8" height="8" rx="2.5" fill="none" stroke="#cbd5e1" strokeWidth="1.4" />
          <polyline
            className="ck2"
            points="133,60 136,63 140,57.5"
            fill="none"
            stroke="#22c55e"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="144" y="58" width="24" height="4.5" rx="2.2" fill="#e2e8f0" className="ls2" />
          <rect x="132" y="70" width="8" height="8" rx="2.5" fill="none" stroke="#cbd5e1" strokeWidth="1.4" />
          <polyline
            className="ck3"
            points="133,74 136,77 140,71.5"
            fill="none"
            stroke="#22c55e"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="144" y="72" width="28" height="4.5" rx="2.2" fill="#e2e8f0" className="ls3" />
          <rect x="132" y="86" width="40" height="4.5" rx="2.2" fill="#e2e8f0" opacity="0.4" />
          <rect x="132" y="98" width="32" height="4.5" rx="2.2" fill="#e2e8f0" opacity="0.3" />
        </g>

        <g className="g-doc" style={{ transform: "rotate(-7deg)", transformOrigin: "78px 112px" }}>
          <rect x="22" y="48" width="118" height="146" rx="9" fill="#c7d4e8" opacity="0.35" />
          <rect x="18" y="44" width="118" height="146" rx="9" fill="#eef1f9" stroke="#dde4f0" strokeWidth="1" />
          <rect x="18" y="44" width="118" height="20" rx="9" fill="#dde4f0" />
          <rect x="18" y="54" width="118" height="10" fill="#dde4f0" />
          <rect x="27" y="50" width="44" height="6" rx="3" fill="#c5cfe4" />
          <rect x="76" y="50" width="30" height="6" rx="3" fill="#c5cfe4" opacity="0.6" />
          <rect x="27" y="72" width="100" height="72" rx="6" fill="#fff" opacity="0.72" />
          <line x1="27" y1="136" x2="127" y2="136" stroke="#e8edf6" strokeWidth="0.8" />
          <line x1="27" y1="124" x2="127" y2="124" stroke="#e8edf6" strokeWidth="0.6" strokeDasharray="3,3" />
          <line x1="27" y1="112" x2="127" y2="112" stroke="#e8edf6" strokeWidth="0.6" strokeDasharray="3,3" />
          <line x1="27" y1="100" x2="127" y2="100" stroke="#e8edf6" strokeWidth="0.6" strokeDasharray="3,3" />
          <circle cx="34" cy="80" r="4" fill="#a78bfa" />
          <rect x="41" y="78" width="18" height="4.5" rx="2.2" fill="#dde4f0" className="ls1" />
          <circle cx="66" cy="80" r="4" fill="#f59e0b" />
          <rect x="73" y="78" width="18" height="4.5" rx="2.2" fill="#dde4f0" className="ls2" />
          <circle cx="98" cy="80" r="4" fill="#ef4444" />
          <rect x="105" y="78" width="14" height="4.5" rx="2.2" fill="#dde4f0" className="ls3" />
          <rect className="bar1" x="38" y="98" width="18" height="38" rx="5" fill="#a78bfa" />
          <rect className="bar2" x="64" y="86" width="18" height="50" rx="5" fill="#f59e0b" />
          <rect className="bar3" x="90" y="76" width="18" height="60" rx="5" fill="#ef4444" />
          <rect x="27" y="152" width="88" height="5.5" rx="2.5" fill="#c5cfe4" className="ls1" />
          <rect x="27" y="162" width="68" height="5.5" rx="2.5" fill="#c5cfe4" className="ls2" />
          <rect x="27" y="172" width="78" height="5.5" rx="2.5" fill="#c5cfe4" className="ls3" />
          <rect x="27" y="183" width="12" height="12" rx="3.5" fill="#fff" stroke="#c5cfe4" strokeWidth="1.6" />
          <polyline
            className="ck1"
            points="29,189 33,193 39,184.5"
            fill="none"
            stroke="#22c55e"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="44" y="185" width="56" height="5.5" rx="2.5" fill="#c5cfe4" className="ls1" />
        </g>

        <g className="g-glass">
          <circle cx="108" cy="132" r="34" fill="rgba(34,197,94,.06)" />
          <circle cx="108" cy="132" r="30" fill="none" stroke="#0d1f35" strokeWidth="9" strokeLinecap="round" />
          <circle className="lens-fill" cx="108" cy="132" r="21" fill="#bfdbfe" opacity="0.55" />
          <circle cx="100" cy="124" r="6" fill="#fff" opacity="0.42" />
          <line x1="130" y1="154" x2="152" y2="176" stroke="#0d1f35" strokeWidth="10" strokeLinecap="round" />
          <circle className="g-pulse" cx="108" cy="134" fill="#22c55e" />
          <circle cx="108" cy="134" r="13" fill="#22c55e" opacity="0.3" />
          <polyline
            className="ck3"
            style={{ strokeDasharray: 24 }}
            points="101,134 107,141 118,125"
            fill="none"
            stroke="#15803d"
            strokeWidth="3.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </svg>
    </div>
  );
}

export default function PortfolioPremiumLoader({ active = false, complete = false }) {
  const [phase_index, set_phase_index] = useState(0);
  const [display_step, set_display_step] = useState(0);
  const [pct, set_pct] = useState(0);
  const pct_frame_ref = useRef(null);
  const pct_value_ref = useRef(0);
  const max_step_ref = useRef(0);
  const max_pct_ref = useRef(0);

  const animate_pct = useCallback((target) => {
    const goal = Math.max(pct_value_ref.current, target, max_pct_ref.current);
    if (goal <= pct_value_ref.current) {
      set_pct(Math.round(pct_value_ref.current));
      return;
    }

    if (pct_frame_ref.current) {
      cancelAnimationFrame(pct_frame_ref.current);
    }

    const start = pct_value_ref.current;
    const delta = (goal - start) / 18;

    const tick = () => {
      let cur = pct_value_ref.current + delta;
      if (cur >= goal) {
        pct_value_ref.current = goal;
        max_pct_ref.current = goal;
        set_pct(Math.round(goal));
        return;
      }
      pct_value_ref.current = cur;
      set_pct(Math.round(cur));
      pct_frame_ref.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const apply_phase_forward = useCallback(
    (index) => {
      const p = LOADER_PHASES[index];
      if (!p) return;

      set_phase_index(index);

      const next_step = Math.max(max_step_ref.current, p.step);
      if (next_step !== max_step_ref.current) {
        max_step_ref.current = next_step;
        set_display_step(next_step);
      }

      const next_pct = Math.max(max_pct_ref.current, p.pct);
      if (next_pct > max_pct_ref.current) {
        max_pct_ref.current = next_pct;
        animate_pct(next_pct);
      }
    },
    [animate_pct]
  );

  useEffect(() => {
    if (!active) {
      if (pct_frame_ref.current) cancelAnimationFrame(pct_frame_ref.current);
      set_phase_index(0);
      set_display_step(0);
      set_pct(0);
      pct_value_ref.current = 0;
      max_step_ref.current = 0;
      max_pct_ref.current = 0;
      return undefined;
    }

    if (complete) {
      max_step_ref.current = COMPLETE_PHASE.step;
      max_pct_ref.current = COMPLETE_PHASE.pct;
      set_display_step(COMPLETE_PHASE.step);
      animate_pct(COMPLETE_PHASE.pct);
      return undefined;
    }

    let phase_timeouts = [];

    const run_cycle = () => {
      phase_timeouts.forEach((id) => window.clearTimeout(id));
      phase_timeouts = PHASE_AT_MS.map((delay_ms, index) =>
        window.setTimeout(() => apply_phase_forward(index), delay_ms)
      );
    };

    run_cycle();
    const interval_id = window.setInterval(run_cycle, LOADER_CYCLE_MS);

    return () => {
      window.clearInterval(interval_id);
      phase_timeouts.forEach((id) => window.clearTimeout(id));
      if (pct_frame_ref.current) cancelAnimationFrame(pct_frame_ref.current);
    };
  }, [active, complete, apply_phase_forward, animate_pct]);

  if (!active) return null;

  const current = complete ? COMPLETE_PHASE : LOADER_PHASES[phase_index];
  const step = complete ? COMPLETE_PHASE.step : display_step;

  return (
    <div
      className="premium_loader_overlay show"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={current?.main}
    >
      <div className="blob blob1" aria-hidden="true" />
      <div className="blob blob2" aria-hidden="true" />

      <div className="premium_loader_content">
        <PremiumLoaderIllustration />

        <div className="text-block">
          <div className="text-main">
            {complete ? (
              <span className="show">{COMPLETE_PHASE.main}</span>
            ) : (
              LOADER_PHASES.map((p, i) => (
                <span key={p.main} className={i === phase_index ? "show" : ""}>
                  {p.main}
                </span>
              ))
            )}
          </div>
          <div className="text-sub">{current?.sub}</div>
        </div>

        <div className="steps-wrap">
          <div className="step-node">
            <div className={step > 0 ? "step-circle done" : "step-circle active"}>
              {step > 0 ? "✓" : "1"}
            </div>
            <div className={step > 0 ? "step-label done" : "step-label active"}>
              Fetch
            </div>
          </div>
          <div className="step-line">
            <div className="step-line-fill" style={{ width: line_fill_width(0, step) }} />
          </div>
          <div className="step-node">
            <div
              className={
                step > 1 ? "step-circle done" : step === 1 ? "step-circle active" : "step-circle"
              }
            >
              {step > 1 ? "✓" : "2"}
            </div>
            <div
              className={
                step > 1 ? "step-label done" : step === 1 ? "step-label active" : "step-label"
              }
            >
              Scan
            </div>
          </div>
          <div className="step-line">
            <div className="step-line-fill" style={{ width: line_fill_width(1, step) }} />
          </div>
          <div className="step-node">
            <div
              className={
                step > 2 ? "step-circle done" : step === 2 ? "step-circle active" : "step-circle"
              }
            >
              {step > 2 ? "✓" : "3"}
            </div>
            <div
              className={
                step > 2 ? "step-label done" : step === 2 ? "step-label active" : "step-label"
              }
            >
              AI
            </div>
          </div>
          <div className="step-line">
            <div className="step-line-fill" style={{ width: line_fill_width(2, step) }} />
          </div>
          <div className="step-node">
            <div className={step >= 4 ? "step-circle done" : step >= 3 ? "step-circle active" : "step-circle"}>
              {step >= 4 ? "✓" : "4"}
            </div>
            <div className={step >= 4 ? "step-label done" : step >= 3 ? "step-label active" : "step-label"}>
              Done
            </div>
          </div>
        </div>

        <div className="bar-wrap">
          <div className="bar-head">
            <span className="bar-head-label">Progress</span>
            <span className="bar-pct">{pct}%</span>
          </div>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${pct}%` }} />
            {!complete && <div className="bar-shimmer" />}
          </div>
        </div>

        {!complete && (
          <div className="dots" aria-hidden="true">
            <div className="dot" />
            <div className="dot" />
            <div className="dot" />
          </div>
        )}
      </div>
    </div>
  );
}
